import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AIPracticeTestSetup, {
  type AIPracticeTestRequest,
} from "@/components/tests/AIPracticeTestSetup";
import TestInterface from "@/components/tests/TestInterface";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  generatePracticeTest,
  type GeneratedTestQuestion,
} from "@/services/practiceTestService";

interface RunningTest {
  questions: GeneratedTestQuestion[];
  request: AIPracticeTestRequest;
}

export default function AIPracticeTest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState<RunningTest | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);

  const handleSubmit = async (req: AIPracticeTestRequest) => {
    setLoading(true);
    try {
      const questions = await generatePracticeTest({
        grade: req.grade,
        subjectId: req.subjectId,
        categoryId: req.categoryId,
        topicTitle: req.topicTitle,
        focusHint: req.focusHint,
        count: req.count,
        difficulty: req.difficulty,
      });
      setRunning({ questions, request: req });
    } catch (err: any) {
      // 429 toast already shown by invokeAI; show a fallback for other errors.
      if (err?.name !== "AIRateLimitError") {
        toast.error(err?.message ?? "Failed to generate practice test");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestSubmit = async (
    results: { questionId: string; selectedOptionId: string }[],
  ) => {
    if (!running) return;

    let score = 0;
    for (const r of results) {
      const q = running.questions.find((x) => x.id === r.questionId);
      const correct = q?.options.find((o) => o.is_correct);
      if (correct && r.selectedOptionId === correct.id) score++;
    }

    const resultsData = {
      questions: running.questions.map((q) => ({
        id: q.id,
        text: q.text,
        explanation: q.explanation,
        options: q.options,
        correctOptionId: q.options.find((o) => o.is_correct)?.id || "",
        categoryName: running.request.categoryName,
      })),
      answers: results.reduce((acc, r) => {
        acc[r.questionId] = r.selectedOptionId;
        return acc;
      }, {} as Record<string, string>),
      categories: [],
      grade: running.request.grade,
      subject: running.request.subjectName,
      categoryName: running.request.categoryName,
      score,
      totalQuestions: running.questions.length,
      isGuest: !user,
      isAIGenerated: true,
    };

    if (!user) {
      toast.success(`You scored ${score}/${running.questions.length}`, {
        description: "Sign up to save your AI practice results.",
      });
      navigate("/results/guest", { state: resultsData });
      return;
    }

    // Signed-in: save a session row only (AI questions are ephemeral, so we
    // can't FK test_answers.question_id to questions.id). Detail still
    // renders from in-memory state on the immediate results view.
    try {
      const { data: session, error } = await supabase
        .from("test_sessions")
        .insert({
          user_id: user.id,
          subject_id: running.request.subjectId,
          grade: running.request.grade,
          total_questions: running.questions.length,
          score,
          time_taken: timeElapsed,
          completed: true,
        })
        .select()
        .single();

      if (error || !session) {
        console.error("Error saving AI test session:", error);
        navigate("/results/guest", { state: resultsData });
        return;
      }

      navigate(`/results/${session.id}`, { state: resultsData });
    } catch (e) {
      console.error("Error submitting AI test:", e);
      navigate("/results/guest", { state: resultsData });
    }
  };

  if (running) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <Seo title="AI Practice Test" description="Take an AI-generated practice test." noindex />
        <TestInterface
          questions={running.questions}
          onSubmit={handleTestSubmit}
          subjectName={running.request.subjectName}
          categoryName={running.request.categoryName ?? undefined}
          testMode="practice"
          onTimeUpdate={setTimeElapsed}
          onBackToSelection={() => setRunning(null)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo
        title="AI Practice Test"
        description="Generate a custom AI practice test by grade, subject, and category."
        canonicalPath="/practice/ai"
      />
      <div className="container mx-auto px-4 py-8">
        <AIPracticeTestSetup
          defaultGrade={params.get("grade") ?? undefined}
          defaultSubjectId={params.get("subjectId") ?? undefined}
          defaultCategoryId={params.get("categoryId") ?? undefined}
          loading={loading}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}
